/**
 * Run logs shaped like the installer's, with nothing real in them.
 *
 * Every shape here was read off four real, redacted logs from the test account
 * (an apply, a plan, a destroy and an `output` run) — the escapes in front of a
 * header, the CRD schemas that make up most of a plan, the ten-second ticks,
 * the installer's timestamped phase lines. **None of their text is.** A real
 * log carries account ids, hostnames and names a customer chose, and a fixture
 * is committed forever, so these are written from the shapes up.
 *
 * Exported from `shared` so the parser's tests, the API's and the console's
 * mock harness all read the same logs. Each is built by a function, so nothing
 * is constructed unless asked for and the console bundle can drop the lot.
 */
const ESC = '\u001b';
const bold = (text) => `${ESC}[1m${text}${ESC}[0m`;
/** A plan header, escaped exactly as Terraform prints one. */
const header = (address, phrase) => `${ESC}[1m  # ${address}${ESC}[0m ${phrase}`;
const stamp = (at, level, message) => `${at} ${level} ${message}`;
const progress = (address, text) => `${ESC}[0m${ESC}[1m${address}: ${text}${ESC}[0m${ESC}[0m`;
const body = (symbol, type, name, lines) => [
    `${ESC}[0m  ${ESC}[32m${symbol}${ESC}[0m${ESC}[0m resource "${type}" "${name}" {`,
    ...lines.map((l) => `      ${l}`),
    '    }',
    '',
];
/**
 * A CRD schema body, which is what makes a real plan sixty thousand lines.
 *
 * Includes a heredoc whose lines are free text — a line of prose, a line that
 * starts with a brace — because that is what no single-line rule can place.
 */
export const crdBody = (name, depth) => body('+', 'kubectl_manifest', name, [
    '+ apply_only = false',
    '+ yaml_body  = (sensitive value)',
    '+ manifest   = {',
    ...Array.from({ length: depth }, (_, i) => `    + "property${String(i)}" = {`),
    '        + description = <<-EOT',
    '            Specifies how the controller reconciles this resource.',
    '            } this line starts with a brace and is still prose.',
    '',
    '            Blank lines inside a heredoc are part of it too.',
    '        EOT',
    ...Array.from({ length: depth }, () => '    }'),
    '  }',
]);
const initialise = (at) => [
    'redactor self-test passed (rules v1)',
    stamp(at(0), 'INFO', 'Starting initialization process...'),
    stamp(at(0), 'DEBUG', 'Copying the working config...'),
    stamp(at(1), 'INFO', 'Initializing Terraform...'),
    '',
    `${ESC}[0m${ESC}[1mInitializing the backend...${ESC}[0m`,
    '- Installing hashicorp/aws v5.0.0...',
    '',
    `${ESC}[0m${ESC}[1m${ESC}[32mTerraform has been successfully initialized!${ESC}[0m${ESC}[32m${ESC}[0m`,
    stamp(at(12), 'INFO', 'Initialization completed successfully.'),
];
/** `2026-01-05T10:00:00Z` plus `s` seconds. */
const clock = (start) => (s) => new Date(Date.parse(start) + s * 1000).toISOString().replace('.000Z', 'Z');
const planIntro = [
    'Terraform used the selected providers to generate the following execution',
    'plan. Resource actions are indicated with the following symbols:',
    `  ${ESC}[32m+${ESC}[0m create`,
    ` ${ESC}[36m<=${ESC}[0m read (data resources)`,
    '',
    'Terraform will perform the following actions:',
    '',
];
const deprecationWarning = [
    `${ESC}[33m╷${ESC}[0m${ESC}[0m`,
    `${ESC}[33m│${ESC}[0m ${ESC}[0m${ESC}[1m${ESC}[33mWarning: ${ESC}[0m${ESC}[0m${ESC}[1mDeprecated attribute${ESC}[0m`,
    `${ESC}[33m│${ESC}[0m ${ESC}[0m`,
    `${ESC}[33m│${ESC}[0m ${ESC}[0m  on locals.tf line 9, in locals:`,
    `${ESC}[33m╵${ESC}[0m${ESC}[0m`,
];
/**
 * A plan that changes things, including two things worth reading twice.
 *
 * 9 creates, 1 update, 3 replaces (one of them the database), 2 destroys (one
 * a bucket), 2 reads — so the `Plan:` line is 12 to add, 1 to change, 5 to
 * destroy, and the headers agree with it.
 */
export const planWithDestroyAndReplace = () => {
    const at = clock('2026-01-05T10:00:00Z');
    return [
        ...initialise(at),
        stamp(at(14), 'INFO', "Executing 'qrvey plan' command..."),
        stamp(at(15), 'WARN', 'A setting was not given; a default is used.'),
        `${ESC}[0m${ESC}[1mdata.http.crd: Reading...${ESC}[0m`,
        `${ESC}[0m${ESC}[1mdata.http.crd: Read complete after 1s [id=https://example.test/crd.yaml]${ESC}[0m`,
        ...planIntro,
        header('data.aws_iam_policy_document.monitor', 'will be read during apply'),
        '  # (config refers to values not yet known)',
        ' <= data "aws_iam_policy_document" "monitor" {',
        '      + id = (known after apply)',
        '    }',
        '',
        header('module.eks.data.aws_iam_policy_document.custom[0]', 'will be read during apply'),
        ' <= data "aws_iam_policy_document" "custom" {',
        '    }',
        '',
        header('module.aurora.aws_rds_cluster.this[0]', 'must be replaced'),
        `${ESC}[0m${ESC}[33m-${ESC}[0m/${ESC}[32m+${ESC}[0m resource "aws_rds_cluster" "this" {`,
        '      ~ engine_version = "16.4" -> "15.2" # forces replacement',
        '        # (40 unchanged attributes hidden)',
        '    }',
        '',
        header('aws_security_group.runner', 'must be replaced'),
        '-/+ resource "aws_security_group" "runner" {',
        '      ~ name = "runner-a" -> "runner-b" # forces replacement',
        '    }',
        '',
        header('module.eks.aws_eks_node_group.this["karpenter"]', 'is tainted, so must be replaced'),
        '-/+ resource "aws_eks_node_group" "this" {',
        '    }',
        '',
        header('aws_s3_bucket.exports["reports-archive"]', 'will be destroyed'),
        '  # (because key ["reports-archive"] is not in for_each map)',
        '  - resource "aws_s3_bucket" "exports" {',
        '      - bucket = "fixture-reports-archive" -> null',
        '    }',
        '',
        header('aws_iam_role_policy_attachment.old', 'will be destroyed'),
        '  - resource "aws_iam_role_policy_attachment" "old" {',
        '    }',
        '',
        header('helm_release.qrvey[0]', 'will be updated in-place'),
        '  ~ resource "helm_release" "qrvey" {',
        '      ~ version = "9.4.0" -> "9.5.0"',
        '    }',
        '',
        header('kubectl_manifest.crd["clusters.example.test"]', 'will be created'),
        ...crdBody('crd', 6),
        header('kubectl_manifest.crd["jobs.example.test"]', 'will be created'),
        ...crdBody('crd', 4),
        header('kubectl_manifest.crd["queues.example.test"]', 'will be created'),
        ...crdBody('crd', 2),
        header('module.eks.aws_eks_addon.this["coredns"]', 'will be created'),
        ...body('+', 'aws_eks_addon', 'this', ['+ addon_name = "coredns"']),
        header('module.eks.aws_eks_addon.this["vpc-cni"]', 'will be created'),
        ...body('+', 'aws_eks_addon', 'this', ['+ addon_name = "vpc-cni"']),
        header('module.aurora.aws_rds_cluster_parameter_group.this[0]', 'will be created'),
        ...body('+', 'aws_rds_cluster_parameter_group', 'this', ['+ family = "aurora-postgresql15"']),
        header('random_password.admin', 'will be created'),
        ...body('+', 'random_password', 'admin', ['+ result = (sensitive value)']),
        header('module.vpc.aws_subnet.private[0]', 'will be created'),
        ...body('+', 'aws_subnet', 'private', ['+ cidr_block = "10.0.1.0/24"']),
        header('module.vpc.aws_subnet.private[1]', 'will be created'),
        ...body('+', 'aws_subnet', 'private', ['+ cidr_block = "10.0.2.0/24"']),
        `${bold('Plan:')} 12 to add, 1 to change, 5 to destroy.`,
        '',
        'Changes to Outputs:',
        '  + env_url = "https://fixture.example.test"',
        '  ~ version = "9.4.0" -> "9.5.0"',
        ...deprecationWarning,
        `${ESC}[J${ESC}[?25h${ESC}[?2004l${stamp(at(80), 'INFO', 'Terraform plan completed.')}`,
    ];
};
/**
 * A fresh apply from start to banner, timed.
 *
 * The phases and their seconds are the assertion: initialise 12s, checks from
 * 14s to 20s, Terraform from 20s to 1600s, readiness to 2200s, routing to
 * 2300s, post-deploy to 2320s, outputs to 2322s.
 */
export const freshApply = () => {
    const at = clock('2026-01-05T11:00:00Z');
    return [
        ...initialise(at),
        stamp(at(14), 'INFO', 'Starting Terraform apply...'),
        stamp(at(15), 'INFO', 'Validating something about the network...'),
        stamp(at(20), 'INFO', 'Running Terraform apply...'),
        ...planIntro,
        header('module.eks.aws_eks_cluster.this[0]', 'will be created'),
        ...body('+', 'aws_eks_cluster', 'this', ['+ name = "fixture-cluster"']),
        header('aws_opensearch_domain.this[0]', 'will be created'),
        ...body('+', 'aws_opensearch_domain', 'this', ['+ domain_name = "fixture"']),
        header('module.aurora.aws_rds_cluster_instance.this["one"]', 'will be created'),
        ...body('+', 'aws_rds_cluster_instance', 'this', ['+ identifier = "fixture-1"']),
        header('null_resource.wait_for_queue[0]', 'will be created'),
        ...body('+', 'null_resource', 'wait_for_queue', ['+ id = (known after apply)']),
        header('time_sleep.wait_for_dns', 'will be created'),
        ...body('+', 'time_sleep', 'wait_for_dns', ['+ create_duration = "300s"']),
        header('random_string.suffix', 'will be created'),
        ...body('+', 'random_string', 'suffix', ['+ length = 8']),
        `${bold('Plan:')} 6 to add, 0 to change, 0 to destroy.`,
        progress('random_string.suffix', 'Creating...'),
        progress('random_string.suffix', 'Creation complete after 0s [id=abcdefgh]'),
        progress('module.eks.aws_eks_cluster.this[0]', 'Creating...'),
        progress('aws_opensearch_domain.this[0]', 'Creating...'),
        progress('module.eks.aws_eks_cluster.this[0]', 'Still creating... [10s elapsed]'),
        progress('aws_opensearch_domain.this[0]', 'Still creating... [10s elapsed]'),
        progress('module.eks.aws_eks_cluster.this[0]', 'Still creating... [9m40s elapsed]'),
        progress('module.eks.aws_eks_cluster.this[0]', 'Creation complete after 9m43s [id=fixture-cluster]'),
        progress('module.aurora.aws_rds_cluster_instance.this["one"]', 'Creating...'),
        progress('module.aurora.aws_rds_cluster_instance.this["one"]', 'Creation complete after 5m55s [id=fixture-1]'),
        progress('aws_opensearch_domain.this[0]', 'Creation complete after 10m41s [id=arn:fixture]'),
        progress('null_resource.wait_for_queue[0]', 'Creating...'),
        progress('null_resource.wait_for_queue[0]', 'Creation complete after 5m18s [id=123]'),
        progress('time_sleep.wait_for_dns', 'Creating...'),
        progress('time_sleep.wait_for_dns', 'Creation complete after 5m0s [id=2026-01-05T11:20:00Z]'),
        ...deprecationWarning,
        '',
        `${ESC}[0m${ESC}[1m${ESC}[32mApply complete! Resources: 6 added, 0 changed, 0 destroyed.${ESC}[0m`,
        '',
        'Outputs:',
        'env_url = "https://fixture.example.test"',
        stamp(at(1600), 'DEBUG', 'Checking pre-set Deployment ID...'),
        stamp(at(1601), 'INFO', 'Configuring kubectl for the cluster...'),
        stamp(at(1610), 'INFO', "Waiting up to 10 minutes for all deployments in namespace 'apps' to be ready..."),
        stamp(at(1700), 'WARN', 'Some deployments are not ready yet.'),
        stamp(at(2200), 'INFO', 'Gateway API Routing Validation'),
        stamp(at(2300), 'INFO', 'Running post-deployment step: first (https://fixture.example.test/a)'),
        stamp(at(2310), 'INFO', 'Running post-deployment step: second (https://fixture.example.test/b)'),
        stamp(at(2320), 'INFO', 'Retrieving and printing deployment details from Terraform outputs...'),
        stamp(at(2322), 'INFO', 'Done.'),
    ];
};
/** A destroy: cleanup first, then Terraform, then the buckets. */
export const destroyRun = () => {
    const at = clock('2026-01-06T09:00:00Z');
    return [
        ...initialise(at),
        stamp(at(15), 'INFO', 'Starting Destroy process...'),
        stamp(at(20), 'INFO', 'Deleting things the installer deletes first...'),
        stamp(at(600), 'INFO', 'Pass 1: terraform destroy -refresh=false...'),
        ...planIntro,
        header('module.eks.aws_eks_cluster.this[0]', 'will be destroyed'),
        '  - resource "aws_eks_cluster" "this" {',
        '    }',
        '',
        header('aws_s3_bucket.files', 'will be destroyed'),
        '  - resource "aws_s3_bucket" "files" {',
        '    }',
        '',
        header('module.vpc.aws_nat_gateway.this[0]', 'will be destroyed'),
        '  - resource "aws_nat_gateway" "this" {',
        '    }',
        '',
        `${bold('Plan:')} 0 to add, 0 to change, 3 to destroy.`,
        progress('module.vpc.aws_nat_gateway.this[0]', 'Destroying... [id=nat-fixture]'),
        progress('module.vpc.aws_nat_gateway.this[0]', 'Destruction complete after 10s'),
        progress('aws_s3_bucket.files', 'Destroying... [id=fixture-files]'),
        progress('aws_s3_bucket.files', 'Destruction complete after 1s'),
        progress('module.eks.aws_eks_cluster.this[0]', 'Destroying... [id=fixture-cluster]'),
        progress('module.eks.aws_eks_cluster.this[0]', 'Still destroying... [id=fixture-cluster, 10s elapsed]'),
        progress('module.eks.aws_eks_cluster.this[0]', 'Destruction complete after 31s'),
        '',
        `${ESC}[0m${ESC}[1m${ESC}[32mDestroy complete! Resources: 3 destroyed.${ESC}[0m`,
        stamp(at(700), 'INFO', 'All resources destroyed on first pass'),
        stamp(at(701), 'INFO', '✅ All infrastructure destroyed EXCEPT the S3 buckets.'),
        stamp(at(740), 'INFO', '=== Post-destroy verification ==='),
        stamp(at(750), 'INFO', 'All resources verified clean.'),
    ];
};
/**
 * The fresh apply, cut off while three things are still being created.
 *
 * What a live panel holds, and what a read stopped at its bound holds.
 */
export const applyCutOffMidway = () => {
    const all = freshApply();
    const cut = all.findIndex((l) => l.includes('Creation complete after 9m43s'));
    return all.slice(0, cut);
};
/**
 * An apply the installer ran twice, dying on the same error each time.
 *
 * Complete — Terraform said how it ended, which was badly — so not `partial`.
 * The cluster was still being created when the error stopped the run.
 */
export const failedApply = () => {
    const at = clock('2026-01-07T08:00:00Z');
    const attempt = (n) => [
        stamp(at(100 * n), 'INFO', 'Running Terraform apply...'),
        ...planIntro,
        header('aws_eks_access_entry.deployer[0]', 'will be created'),
        ...body('+', 'aws_eks_access_entry', 'deployer', ['+ principal_arn = (known after apply)']),
        header('module.eks.aws_eks_cluster.this[0]', 'will be created'),
        ...body('+', 'aws_eks_cluster', 'this', ['+ name = "fixture-cluster"']),
        `${bold('Plan:')} 2 to add, 0 to change, 0 to destroy.`,
        progress('module.eks.aws_eks_cluster.this[0]', 'Creating...'),
        progress('aws_eks_access_entry.deployer[0]', 'Creating...'),
        progress('module.eks.aws_eks_cluster.this[0]', 'Still creating... [40s elapsed]'),
        `${ESC}[31m╷${ESC}[0m${ESC}[0m`,
        `${ESC}[31m│${ESC}[0m ${ESC}[0m${ESC}[1m${ESC}[31mError: ${ESC}[0m${ESC}[0m${ESC}[1mcreating access entry: ResourceInUseException, RequestID: 0000000${String(n)}-0000-4000-8000-000000000000${ESC}[0m`,
        `${ESC}[31m│${ESC}[0m ${ESC}[0m`,
        `${ESC}[31m│${ESC}[0m ${ESC}[0m${ESC}[0m  with aws_eks_access_entry.deployer[0],`,
        `${ESC}[31m│${ESC}[0m ${ESC}[0m  on main.tf line 896, in resource "aws_eks_access_entry" "deployer":`,
        `${ESC}[31m╵${ESC}[0m${ESC}[0m`,
        stamp(at(100 * n + 60), 'ERROR', 'Terraform apply failed.'),
    ];
    return [
        ...initialise(at),
        stamp(at(14), 'INFO', 'Starting Terraform apply...'),
        ...attempt(1),
        ...attempt(2),
    ];
};
/** An `output` run: the installer talks, Terraform plans nothing. */
export const outputOnly = () => {
    const at = clock('2026-01-08T08:00:00Z');
    return [
        ...initialise(at),
        stamp(at(14), 'INFO', 'Retrieving Terraform outputs...'),
        'env_url = "https://fixture.example.test"',
        stamp(at(16), 'INFO', 'Successfully connected.'),
    ];
};
/**
 * A plan of `resources` creates, in `componentCount` modules, with the CRD
 * bodies that make real ones long — about `lines` lines in all.
 *
 * For the performance test and for the stored-size cap: shape-real, and as
 * large as asked.
 */
export const generatedPlan = (options) => {
    const at = clock('2026-01-09T08:00:00Z');
    const components = options.componentCount ?? 20;
    const depth = options.bodyLines ?? 120;
    const pad = 'x'.repeat(options.addressPadding ?? 0);
    const out = [...initialise(at), stamp(at(14), 'INFO', 'Starting Terraform apply...')];
    out.push(stamp(at(20), 'INFO', 'Running Terraform apply...'), ...planIntro);
    for (let i = 0; i < options.resources; i += 1) {
        const address = `module.m${String(i % components)}.kubectl_manifest.crd${pad}["r${String(i)}"]`;
        out.push(header(address, 'will be created'));
        out.push(...crdBody('crd', Math.max(1, Math.floor(depth / 2) - 6)));
    }
    out.push(`${bold('Plan:')} ${String(options.resources)} to add, 0 to change, 0 to destroy.`);
    for (let i = 0; i < options.resources; i += 1) {
        const address = `module.m${String(i % components)}.kubectl_manifest.crd${pad}["r${String(i)}"]`;
        out.push(progress(address, 'Creating...'));
        out.push(progress(address, `Creation complete after ${String(i % 600)}s [id=${String(i)}]`));
    }
    out.push(`Apply complete! Resources: ${String(options.resources)} added, 0 changed, 0 destroyed.`);
    out.push(stamp(at(4000), 'INFO', 'Done.'));
    return out;
};
/**
 * A long apply that failed, for the log viewer (IM-166).
 *
 * Everything the viewer has to handle in one log: one resource body far longer
 * than all the rest together (a real apply had one of 19,091 lines), ordinary
 * small bodies, ten-second ticks by the dozen, a framed warning, the compact
 * `Warnings:` list, an installer `WARN`, escapes in front of the headers and
 * the progress lines, and a framed error with its resource and file:line
 * followed by the installer's own `ERROR`. `bigBodyLines` is roughly the
 * length of the one big body; the mock harness asks for a real-sized one, the
 * tests for something small.
 */
export const longFailedApply = (options = {}) => {
    const at = clock('2026-01-10T08:00:00Z');
    const big = options.bigBodyLines ?? 400;
    const ticks = options.ticks ?? 30;
    const small = ['aws_iam_role.runner', 'aws_security_group.cluster', 'random_string.suffix'];
    return [
        ...initialise(at),
        stamp(at(14), 'INFO', 'Starting Terraform apply...'),
        stamp(at(15), 'WARN', 'A setting was not given; a default is used.'),
        stamp(at(20), 'INFO', 'Running Terraform apply...'),
        ...planIntro,
        header('kubectl_manifest.crd["schemas.example.test"]', 'will be created'),
        ...crdBody('crd', Math.max(1, Math.floor((big - 12) / 2))),
        ...small.flatMap((address) => [
            header(address, 'will be created'),
            ...body('+', address.split('.')[0] ?? '', address.split('.')[1] ?? '', [
                '+ id = (known after apply)',
            ]),
        ]),
        header('module.eks.aws_eks_cluster.this[0]', 'will be created'),
        ...body('+', 'aws_eks_cluster', 'this', ['+ name = "fixture-cluster"']),
        `${bold('Plan:')} 5 to add, 0 to change, 0 to destroy.`,
        '',
        'Warnings:',
        '- Argument is deprecated',
        '  (and one more similar warning elsewhere)',
        '',
        ...small.flatMap((address) => [
            progress(address, 'Creating...'),
            progress(address, 'Creation complete after 1s [id=fixture]'),
        ]),
        progress('kubectl_manifest.crd["schemas.example.test"]', 'Creating...'),
        progress('kubectl_manifest.crd["schemas.example.test"]', 'Creation complete after 2s [id=crd]'),
        progress('module.eks.aws_eks_cluster.this[0]', 'Creating...'),
        ...Array.from({ length: ticks }, (_, i) => progress('module.eks.aws_eks_cluster.this[0]', `Still creating... [${String((i + 1) * 10)}s elapsed]`)),
        ...deprecationWarning,
        `${ESC}[31m╷${ESC}[0m${ESC}[0m`,
        `${ESC}[31m│${ESC}[0m ${ESC}[0m${ESC}[1m${ESC}[31mError: ${ESC}[0m${ESC}[0m${ESC}[1mwaiting for EKS Cluster (fixture-cluster) create: timeout while waiting for state to become 'ACTIVE'${ESC}[0m`,
        `${ESC}[31m│${ESC}[0m ${ESC}[0m`,
        `${ESC}[31m│${ESC}[0m ${ESC}[0m${ESC}[0m  with module.eks.aws_eks_cluster.this[0],`,
        `${ESC}[31m│${ESC}[0m ${ESC}[0m  on eks.tf line 12, in resource "aws_eks_cluster" "this":`,
        `${ESC}[31m╵${ESC}[0m${ESC}[0m`,
        stamp(at(2000), 'ERROR', 'Terraform apply failed.'),
    ];
};
//# sourceMappingURL=synthetic-logs.js.map